import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MAX_ATTEMPTS,
  WORD_LENGTH,
  mergeKeyboardStates,
  type GuessEntry,
  type LetterState,
} from "@mk/shared";
import { ApiError, api, post } from "../../lib/api.ts";
import { useBoardInput } from "../useBoardInput.ts";
import { useSession } from "../../lib/session.ts";
import { gameEdition } from "../../lib/edition.ts";
import { messages } from "../../i18n/index.ts";
import { playSound } from "../../lib/sound.ts";
import { recordCompletedGame } from "../../lib/adGate.ts";

interface DailyView {
  dayKey: string;
  board: GuessEntry[];
  solved: boolean;
  finished: boolean;
  answer: string | null;
}

export type DailyStatus = "loading" | "error" | "playing" | "finished";

const REVEAL_MS = WORD_LENGTH * 180 + 520;

/**
 * Günlük kelime tamamen sunucuda doğrulanır — cevap bitmeden istemciye gelmez.
 * Tahta sunucudan geldiği için başka cihazdan devam etmek de mümkün.
 */
export function useDailyGame() {
  const [status, setStatus] = useState<DailyStatus>("loading");
  const [view, setView] = useState<DailyView | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [revealed, setRevealed] = useState(true);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  const timers = useRef<number[]>([]);
  const refreshStats = useSession((s) => s.refreshStats);

  const clearTimers = () => {
    for (const id of timers.current) window.clearTimeout(id);
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const warn = useCallback((message: string) => {
    setToast(message);
    setShake(true);
    timers.current.push(window.setTimeout(() => setToast(null), 1600));
    timers.current.push(window.setTimeout(() => setShake(false), 440));
  }, []);

  const apply = useCallback(
    (next: DailyView, animate: boolean) => {
      setView(next);
      setStatus(next.finished ? "finished" : "playing");
      if (next.finished && animate) {
        recordCompletedGame(`daily:${next.dayKey}`);
        if (next.solved) playSound("solved");
        setRevealed(false);
        timers.current.push(window.setTimeout(() => setRevealed(true), REVEAL_MS));
        void refreshStats();
      }
    },
    [refreshStats],
  );

  useEffect(() => {
    let cancelled = false;
    api<DailyView>("/api/solo/daily")
      .then((data) => {
        if (!cancelled) apply(data, false);
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const submit = useCallback(
    (raw: string | null) => {
      if (raw === null) {
        warn(messages.feedback.lengthRequired(WORD_LENGTH));
        return;
      }
      const word = gameEdition.normalizeWord(raw);
      if (sendingRef.current || !view) return;
      sendingRef.current = true;
      setSending(true);
      post<DailyView>("/api/solo/daily/guess", { word, row: view.board.length })
        .then((next) => apply(next, true))
        .catch((err: unknown) => {
          warn(err instanceof ApiError ? err.message : messages.feedback.guessFailed);
        })
        .finally(() => {
          sendingRef.current = false;
          setSending(false);
        });
    },
    [apply, view, warn],
  );

  const input = useBoardInput({
    disabled: status !== "playing" || sending,
    onSubmit: submit,
  });

  // Sunucu tahtayı kabul ettiğinde aktif satır boşalsın.
  const rowCount = view?.board.length ?? 0;
  const clearDraft = input.clear;
  useEffect(() => {
    clearDraft();
    if (sendingRef.current) {
      sendingRef.current = false;
      setSending(false);
    }
  }, [rowCount, clearDraft]);

  const keyboard = useMemo(
    () =>
      (view?.board ?? []).reduce<Record<string, LetterState>>(
        (acc, row) => mergeKeyboardStates(acc, row.word, row.pattern),
        {},
      ),
    [view],
  );

  return {
    status,
    /** Tahmin sunucuda; klavye bu sırada tepki vermiyor ve bunu göstermeli. */
    sending,
    dayKey: view?.dayKey ?? "",
    rows: view?.board ?? [],
    answer: view?.answer ?? "",
    solved: view?.solved ?? false,
    attemptsLeft: MAX_ATTEMPTS - (view?.board.length ?? 0),
    cells: input.cells,
    pinned: input.pinned,
    selectCell: input.selectCell,
    press: input.press,
    keyboard,
    toast,
    shake,
    revealed,
  };
}
